import React from 'react'
import { InferType } from 'yup'
import { useMutation } from '@tanstack/react-query'
import { Modal } from '../../../common/Modal'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { ToastType } from '../../../common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ReportProblemSettingsForm } from '../ReportProblemSettingsForm'
import { societyReportSettingsSchema } from '../../../../forms/schemas/society_report_settings'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { patchSociety } from '../../../../api/societies'

interface ReportProblemSettingsModalProps {
  show: boolean
  close: () => void
}

export const ReportProblemSettingsModal = ({
  show,
  close,
}: ReportProblemSettingsModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { society } = useCurrentSociety()
  const { setToastNotification } = useToastNotifications()

  const showSuccessMessage = (): void => {
    setToastNotification(
      ToastType.SUCCESS,
      translate('flashMessage.changesSaved')
    )
  }

  const showErrorMessage = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.changesNotSavedError')
    )
  }

  const { mutate: mutatePatchSociety, isPending: isPatchingSociety } =
    useMutation({
      mutationFn: patchSociety,
      onSuccess: () => {
        close()
        showSuccessMessage()
      },
      onError: () => {
        showErrorMessage()
      },
    })

  const onSubmit = async (
    data: InferType<typeof societyReportSettingsSchema>
  ): Promise<void> => {
    const recipients = [
      data.recipient,
      ...(data.recipientsCC ? data.recipientsCC.map((cc) => cc.email) : []),
    ]
    society &&
      mutatePatchSociety({
        id: society._id,
        body: {
          reportProblemEmailCC: recipients,
        },
      })
  }

  const onError = (): void => {
    showErrorMessage()
  }

  const loading = isPatchingSociety

  return (
    <>
      <Modal show={show} size="md">
        {{
          header: (
            <ModalHeader onClose={close}>
              {translate('reportProblemSettings.modal.title')}
            </ModalHeader>
          ),
          body: (
            <ModalBody>
              {society && (
                <ReportProblemSettingsForm
                  society={society}
                  onError={onError}
                  onSubmit={onSubmit}
                  onClose={close}
                  loading={loading}
                />
              )}
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
